import { useEffect, useState } from 'react';
import api from '../../api';

function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    display_order: 0,
    images: [],
    catalog_pdf: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/categories');
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        display_order: category.display_order || 0,
        images: category.images || [],
        catalog_pdf: category.catalog_pdf || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        display_order: categories.length,
        images: [],
        catalog_pdf: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingCategory ? prev.slug : generateSlug(name)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/api/admin/categories/${editingCategory.id}`, formData);
      } else {
        await api.post('/api/admin/categories', formData);
      }
      closeModal();
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save category');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await api.post('/api/admin/upload', fd, { headers: { 'Content-Type': undefined } });
      setFormData(prev => ({ ...prev, images: [...prev.images, res.data.url] }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPdf(true);
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      const res = await api.post('/api/admin/upload-pdf', fd, { headers: { 'Content-Type': undefined } });
      setFormData(prev => ({ ...prev, catalog_pdf: res.data.url }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const clearPdf = () => {
    setFormData(prev => ({ ...prev, catalog_pdf: '' }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? Products in this category will become uncategorized.')) return;
    try {
      await api.delete(`/api/admin/categories/${id}`);
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete category');
    }
  };

  if (loading) {
    return <div className="loading">Loading categories...</div>;
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Categories</h1>
        <button onClick={() => openModal()} className="btn btn-primary">
          + Add Category
        </button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Images</th>
              <th>PDF</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id}>
                <td>{category.display_order}</td>
                <td>{category.name}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{category.slug}</td>
                <td>{(category.images || []).length || '-'}</td>
                <td>{category.catalog_pdf ? 'Sim' : '-'}</td>
                <td>
                  <button onClick={() => openModal(category)} className="btn btn-small btn-secondary" style={{ marginRight: '5px' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(category.id)} className="btn btn-small btn-danger">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No categories yet. Click "Add Category" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleNameChange(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={e => setFormData({ ...formData, slug: e.target.value })}
                  required
                />
                <small style={{ color: 'var(--text-secondary)' }}>URL-friendly identifier (e.g., "wedding-suits")</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group">
                <label>Images (shown on category page, 6 or more)</label>
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploadingImage} />
                {uploadingImage && <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>Uploading…</span>}
                {formData.images?.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                    {formData.images.map((url, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={url} alt="" style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '4px' }} />
                        <button type="button" onClick={() => removeImage(i)} style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, borderRadius: '50%', background: '#e74c3c', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Catalog PDF (full-width viewer at bottom of category page)</label>
                <input type="file" accept=".pdf,application/pdf" onChange={handlePdfUpload} disabled={uploadingPdf} />
                {uploadingPdf && <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>Uploading…</span>}
                {formData.catalog_pdf && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Current PDF set</span>
                    <button type="button" onClick={clearPdf} className="btn btn-small btn-secondary" style={{ marginLeft: '10px' }}>Remove PDF</button>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCategory ? 'Save Changes' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManager;
